import { useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { post } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { isNullOrWhitespace } from "@common/stringOperations";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useLinkedControls } from "../../riskControls/RiskControls.hooks";
import { useRiskScoringForm } from "../../riskScoring/useRiskScoringForm";
import { formatExposureValue } from "../../riskExposure/formatExposureValue";
import { RiskExposureMode } from "../../riskExposure/RiskExposure.types";
import { IAssessDialog } from "./AssessDialog";
import { ReconfirmRiskAssessmentModel, RiskAssessmentViewModel, ScoreRiskAssessmentModel } from "./AssessDialog.types";
import { buildCurrentAssessmentCard } from "./AssessDialog.utils";

export function useAssessDialog(props: IAssessDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString, translateNumber } = useTranslation();

    const scoringForm = useRiskScoringForm(props.riskAreaId);

    // The scenario row's id is the RiskAssessment head id.
    const prefillQuery = useDotLegalQuery<RiskAssessmentViewModel>(`/risk-assessments/${props.row.id}`);
    const isLoading = scoringForm.isLoading || prefillQuery.isLoading;

    // Control links are staged, not written through: they commit with the scores in the same request, so closing
    // or going back leaves the stored coverage untouched. Null means untouched, so the saved set shows until the
    // user actually edits it, and the edited set survives a prefill refetch.
    const [stagedControlIds, setStagedControlIds] = useState<Array<string> | null>(null);
    const savedControlIds = prefillQuery.data?.frameworkControlIds ?? [];
    const linkedControlIds = stagedControlIds ?? savedControlIds;
    const { availableControls: allControls, showLinkedControls } = useLinkedControls(linkedControlIds);

    // A control on the current open plan is that treatment round's own scope, not standing coverage; the
    // backend strips such ids from coverage writes, so the picker excludes them up front rather than letting
    // the selection silently drop on save. Mirrors the mitigation dialog's exclusion in the other direction.
    const openPlanControlIds = prefillQuery.data?.openPlanControlIds ?? [];
    const availableControls = allControls.filter((control) => !openPlanControlIds.includes(control.id) || linkedControlIds.includes(control.id));

    const [reviewNote, setReviewNote] = useState("");
    const [gateChoice, setGateChoice] = useState<"reconfirm" | "reassess" | null>(null);

    // An unscored head has nothing to reconfirm, so it opens straight on the empty form; a scored head opens on
    // the gate (reference card + Reconfirm/Reassess) and only reveals the note or form once a choice is made.
    const hasCurrentAssessment = prefillQuery.data?.consequence != null;
    const stage: "gate" | "reconfirm" | "reassess" = !hasCurrentAssessment ? "reassess" : (gateChoice ?? "gate");

    // Per-dimension carry-forward: copy the previous revision's rating AND its rationale (plus that dimension's
    // chips) into the empty form, so a dimension that genuinely did not move can be kept without retyping while
    // the moved dimension is still authored fresh.
    const keepPreviousConsequence = () => {
        const prefill = prefillQuery.data;
        if (prefill === undefined) return;
        scoringForm.setConsequence(prefill.consequence);
        scoringForm.setConsequenceJustification(prefill.consequenceJustification ?? "");
        scoringForm.setSelectedConsequenceTypeIds(
            prefill.consequenceTypeIds.filter((id) => scoringForm.consequenceTypeOptions.some((option) => option.id === id))
        );
    };

    const keepPreviousProbability = () => {
        const prefill = prefillQuery.data;
        if (prefill === undefined) return;
        scoringForm.setProbability(prefill.probability);
        scoringForm.setProbabilityJustification(prefill.probabilityJustification ?? "");
        scoringForm.setSelectedMeasureIds(prefill.measureIds.filter((id) => scoringForm.measureOptions.some((option) => option.id === id)));
    };

    // One selection serves both stages: the gate makes reconfirm and reassess exclusive, and backToGate resets the
    // form between them. Filtered against the picker's options, which drops deleted users and the current assessor
    // if they took part last time.
    const keepPreviousParticipants = () =>
        scoringForm.setSelectedParticipantIds(
            (prefillQuery.data?.participants ?? [])
                .map((participant) => participant.id)
                .filter((id) => scoringForm.participantOptions.some((option) => option.id === id))
        );

    // Exposure has no per-dimension keep-link — the previous value (already frame-gated by the backend) seeds
    // the form when reassessing, and unchecking the box drops it.
    const seedExposureFromPrefill = () => {
        const exposureValue = prefillQuery.data?.exposureValue;
        if (exposureValue == null) return;
        scoringForm.setCaptureExposure(true);
        scoringForm.setExposureValueText(exposureValue.toString());
    };

    const saveMutation = useDotLegalMutation(
        () => {
            const model: ScoreRiskAssessmentModel = {
                consequence: scoringForm.consequence!,
                probability: scoringForm.probability!,
                consequenceJustification: scoringForm.consequenceJustification,
                probabilityJustification: scoringForm.probabilityJustification,
                consequenceTypeIds: scoringForm.selectedConsequenceTypeIds,
                measureIds: scoringForm.selectedMeasureIds,
                exposureValue: scoringForm.exposureValue,
                confidence: scoringForm.confidence,
                participantIds: scoringForm.selectedParticipantIds,
                frameworkControlIds: linkedControlIds,
            };
            return post<string>(`/risk-assessments/${props.row.id}/score`, model);
        },
        {
            onSuccess: (response: Result<string>) => {
                if (response.success()) {
                    snackbar.show(translateString("assessmentSaved", { name: props.row.name }));
                    props.onSaved();
                    props.onClose();
                }
            },
        }
    );

    const reconfirmMutation = useDotLegalMutation((model: ReconfirmRiskAssessmentModel) => post<string>(`/risk-assessments/${props.row.id}/reconfirm`, model), {
        onSuccess: (response: Result<string>) => {
            if (response.success()) {
                snackbar.show(translateString("assessmentReconfirmed", { name: props.row.name }));
                props.onSaved();
                props.onClose();
            }
        },
    });

    const reconfirm = () =>
        reconfirmMutation.mutateAsync({ reviewNote: isNullOrWhitespace(reviewNote) ? null : reviewNote, participantIds: scoringForm.selectedParticipantIds });

    const validateAndSave = () => {
        if (scoringForm.validate()) saveMutation.mutateAsync();
    };

    // Returning to the gate discards everything typed on the reassess/reconfirm stage, so reopening either
    // stage starts clean — entered-but-abandoned scores never linger behind the gate.
    const backToGate = () => {
        setGateChoice(null);
        scoringForm.reset();
        setReviewNote("");
        setStagedControlIds(null);
    };

    const currentAssessment = buildCurrentAssessmentCard(
        prefillQuery.data,
        scoringForm.riskArea,
        scoringForm.consequenceTypeOptions,
        scoringForm.measureOptions,
        props.row.assessedByName,
        props.row.assessmentDateLabel,
        props.row.note,
        scoringForm.exposureEnabled,
        translateString(scoringForm.exposureMode === RiskExposureMode.Financial ? "financialRiskExposure" : "estimatedExposureScale"),
        formatExposureValue(
            prefillQuery.data?.exposureValue ?? null,
            scoringForm.exposureMode,
            scoringForm.exposureCurrencyCode,
            scoringForm.exposureScaleUpperBound,
            translateNumber
        ),
        // Resolved against the unfiltered pickable set: the open-plan exclusion narrows what the picker offers,
        // and a control that is also on the open plan drops out of it, which would blank the chip the card is
        // meant to keep showing.
        allControls
    );

    return {
        isLoading,
        scoringForm,
        stage,
        currentAssessment,
        startReconfirm: () => setGateChoice("reconfirm"),
        startReassess: () => {
            setGateChoice("reassess");
            seedExposureFromPrefill();
            // Optional and easy to overlook, so an unseeded dropdown would silently clear the stated confidence.
            scoringForm.setConfidence(prefillQuery.data?.confidence ?? null);
            // Participants are not seeded: attendance is attested per event, so it only arrives through the link.
        },
        backToGate,
        reviewNote,
        setReviewNote,
        reconfirm,
        reconfirmInProgress: reconfirmMutation.isLoading,
        keepPreviousConsequence,
        keepPreviousProbability,
        keepPreviousParticipants,
        validateAndSave,
        inProgress: saveMutation.isLoading,
        showLinkedControls,
        availableControls,
        linkedControlIds,
        onControlsChange: setStagedControlIds,
    };
}
